import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { formatInTz } from '../lib/timezones';

type FilterOperator = 'contains' | 'equals' | 'not_contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';

interface ColumnFilterState {
  operator: FilterOperator;
  text: string;
  selectedValues: string[] | null;
}

interface AdvancedResultsGridProps {
  columns: string[];
  rows: unknown[][];
  storageKey: string;
  canFilterColumns?: boolean;
  canResizeColumns?: boolean;
  onFilteredRowsChange?: (rows: unknown[][]) => void;
}

const FILTER_OPERATORS: Array<{ value: FilterOperator; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

function isTimeColumn(col: string): boolean {
  return /time|datetime|timestamp/i.test(col);
}

// Sentinel's own Log Analytics results grid always displays timestamps in
// UTC (never the viewer's local time) with a "(UTC)" suffix on the column
// header — match that instead of converting to the customer's timezone, so
// analysts can cross-reference timestamps directly against Sentinel.
function formatCell(col: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  const s = String(value);
  if (isTimeColumn(col)) {
    const normalized = s.replace(/(\.[0-9]{3})[0-9]+/, '$1');
    const d = new Date(normalized);
    if (!isNaN(d.getTime()) && s.includes('T') && s.includes(':')) {
      return formatInTz(d, 'UTC', {
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
        hour12: true,
      });
    }
  }
  return s;
}

function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function defaultFilter(): ColumnFilterState {
  return { operator: 'contains', text: '', selectedValues: null };
}

function isLongTextColumn(column: string): boolean {
  return /(raw|json|properties|property|entities|entity|description|details|message|command|commandline|url|query|data|evidence|additional|extended)/i.test(column);
}

function columnMaxWidth(column: string): number {
  return isLongTextColumn(column) ? 650 : 420;
}

function longestLineLength(value: string): number {
  return value
    .split(/\r?\n/)
    .reduce((max, line) => Math.max(max, line.length), 0);
}

function estimateTextWidth(value: string): number {
  const chars = Math.min(longestLineLength(value), 120);
  return Math.ceil(chars * 7.4) + 38;
}

function matchesTextFilter(rawValue: string, filter: ColumnFilterState): boolean {
  const value = rawValue.trim();
  const term = filter.text.trim().toLowerCase();
  const lower = value.toLowerCase();

  if (filter.operator === 'is_empty') return value.length === 0;
  if (filter.operator === 'is_not_empty') return value.length > 0;
  if (!term) return true;

  switch (filter.operator) {
    case 'equals':
      return lower === term;
    case 'not_contains':
      return !lower.includes(term);
    case 'starts_with':
      return lower.startsWith(term);
    case 'ends_with':
      return lower.endsWith(term);
    case 'contains':
    default:
      return lower.includes(term);
  }
}

function isFilterActive(filter: ColumnFilterState | undefined, allValues?: string[]): boolean {
  if (!filter) return false;
  if (filter.operator === 'is_empty' || filter.operator === 'is_not_empty') return true;
  if (filter.text.trim()) return true;
  if (filter.selectedValues !== null) {
    const allCount = allValues?.length ?? 0;
    return filter.selectedValues.length !== allCount;
  }
  return false;
}

function loadWidths(storageKey: string): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const width = Number(value);
      if (Number.isFinite(width) && width >= 60 && width <= 1000) out[key] = width;
    }
    return out;
  } catch {
    return {};
  }
}

function saveWidths(storageKey: string, widths: Record<string, number>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // localStorage can be unavailable in hardened browsers; ignore gracefully.
  }
}

export function AdvancedResultsGrid({
  columns,
  rows,
  storageKey,
  canFilterColumns = true,
  canResizeColumns = true,
  onFilteredRowsChange,
}: AdvancedResultsGridProps) {
  const [openColumnMenu, setOpenColumnMenu] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});
  const [valueSearch, setValueSearch] = useState<Record<string, string>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => loadWidths(storageKey));
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    setColumnWidths(loadWidths(storageKey));
  }, [storageKey]);

  useEffect(() => {
    saveWidths(storageKey, columnWidths);
  }, [storageKey, columnWidths]);

  const uniqueValuesByColumn = useMemo(() => {
    const out: Record<string, string[]> = {};
    columns.forEach((column, columnIndex) => {
      const values = new Set<string>();
      for (const row of rows.slice(0, 5000)) {
        values.add(stringifyCell(row[columnIndex]).trim());
        if (values.size >= 500) break;
      }
      out[column] = Array.from(values).sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [columns, rows]);

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([column, filter]) =>
      isFilterActive(filter, uniqueValuesByColumn[column]),
    );

    if (activeFilters.length === 0) return rows;

    return rows.filter((row) => {
      for (const [column, filter] of activeFilters) {
        const columnIndex = columns.indexOf(column);
        if (columnIndex < 0) continue;
        const rawValue = stringifyCell(row[columnIndex]).trim();

        if (filter.selectedValues !== null && !filter.selectedValues.includes(rawValue)) {
          return false;
        }

        if (!matchesTextFilter(rawValue, filter)) {
          return false;
        }
      }
      return true;
    });
  }, [columns, filters, rows, uniqueValuesByColumn]);

  useEffect(() => {
    onFilteredRowsChange?.(filteredRows);
  }, [filteredRows, onFilteredRowsChange]);

  // Click-to-sort, matching Sentinel's Log Analytics grid. Numeric columns
  // sort numerically, time-like columns sort chronologically, everything
  // else falls back to a locale string compare.
  const sortedRows = useMemo(() => {
    if (!sortColumn) return filteredRows;
    const columnIndex = columns.indexOf(sortColumn);
    if (columnIndex < 0) return filteredRows;
    const dir = sortDirection === 'asc' ? 1 : -1;
    const timeCol = isTimeColumn(sortColumn);
    return [...filteredRows].sort((a, b) => {
      const av = stringifyCell(a[columnIndex]).trim();
      const bv = stringifyCell(b[columnIndex]).trim();
      if (av === '' && bv === '') return 0;
      if (av === '') return 1;
      if (bv === '') return -1;
      if (timeCol) {
        const ad = new Date(av).getTime();
        const bd = new Date(bv).getTime();
        if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * dir;
      }
      const an = Number(av);
      const bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
      return av.localeCompare(bv) * dir;
    });
  }, [filteredRows, columns, sortColumn, sortDirection]);

  function toggleSort(column: string) {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }
    setSortColumn(null);
    setSortDirection('asc');
  }

  function copyCell(key: string, text: string) {
    navigator.clipboard.writeText(text).catch(() => null);
    setCopiedKey(key);
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1200);
  }

  function toggleRowExpanded(rowIndex: number) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }

  useEffect(() => () => { if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current); }, []);

  function patchFilter(column: string, patch: Partial<ColumnFilterState>) {
    setFilters((prev) => {
      const nextFilter: ColumnFilterState = { ...defaultFilter(), ...(prev[column] ?? {}), ...patch };
      const next = { ...prev };
      if (isFilterActive(nextFilter, uniqueValuesByColumn[column])) {
        next[column] = nextFilter;
      } else {
        delete next[column];
      }
      return next;
    });
  }

  function clearFilter(column: string) {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
    setValueSearch((prev) => ({ ...prev, [column]: '' }));
  }

  function clearAllFilters() {
    setFilters({});
    setValueSearch({});
  }

  function toggleValue(column: string, value: string) {
    const allValues = uniqueValuesByColumn[column] ?? [];
    const current = filters[column] ?? defaultFilter();
    const selected = current.selectedValues === null ? allValues : current.selectedValues;
    const nextSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value].sort((a, b) => a.localeCompare(b));
    patchFilter(column, { selectedValues: nextSelected.length === allValues.length ? null : nextSelected });
  }

  function autosizeColumn(column: string, inputRows: unknown[][] = filteredRows) {
    const columnIndex = columns.indexOf(column);
    if (columnIndex < 0) return;

    const minWidth = 90;
    const maxWidth = columnMaxWidth(column);
    let width = estimateTextWidth(column) + 32;
    for (const row of inputRows.slice(0, 200)) {
      width = Math.max(width, estimateTextWidth(stringifyCell(row[columnIndex])));
      if (width >= maxWidth) break;
    }
    width = Math.min(Math.max(width, minWidth), maxWidth);
    setColumnWidths((prev) => ({ ...prev, [column]: width }));
  }

  function autosizeAllColumns() {
    const next: Record<string, number> = {};
    for (const column of columns) {
      const columnIndex = columns.indexOf(column);
      const minWidth = 90;
      const maxWidth = columnMaxWidth(column);
      let width = estimateTextWidth(column) + 32;
      for (const row of filteredRows.slice(0, 200)) {
        width = Math.max(width, estimateTextWidth(stringifyCell(row[columnIndex])));
        if (width >= maxWidth) break;
      }
      next[column] = Math.min(Math.max(width, minWidth), maxWidth);
    }
    setColumnWidths(next);
  }

  function resetColumn(column: string) {
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }

  function resetAllColumns() {
    setColumnWidths({});
  }

  const activeFilterCount = Object.entries(filters).filter(([column, filter]) =>
    isFilterActive(filter, uniqueValuesByColumn[column]),
  ).length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>Click a column header to sort · click a cell to copy its value (Ctrl)</span>
        {activeFilterCount > 0 && (
          <span className="rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900 px-2 py-1">
            {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows after {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
          </span>
        )}
        {activeFilterCount > 0 && canFilterColumns && (
          <button type="button" className="underline hover:text-foreground" onClick={clearAllFilters}>Clear all filters</button>
        )}
        {Object.keys(columnWidths).length > 0 && canResizeColumns && (
          <button type="button" className="underline hover:text-foreground" onClick={resetAllColumns}>Reset all column widths</button>
        )}
      </div>

      <div className="overflow-auto border border-border rounded-lg max-h-[620px]">
        <table className="min-w-full text-[11px] border-separate border-spacing-0">
          <thead className="sticky top-0 bg-muted z-20">
            <tr>
              <th className="w-7 border-b border-border bg-muted px-1 py-1.5" aria-hidden="true" />
              {columns.map((column) => {
                const filter = filters[column];
                const allValues = uniqueValuesByColumn[column] ?? [];
                const active = isFilterActive(filter, allValues);
                const width = columnWidths[column];
                const sorted = sortColumn === column;
                const sortIndicator = sorted ? (sortDirection === 'asc' ? '↑' : '↓') : '↕';

                return (
                  <th
                    key={column}
                    className="text-left font-bold text-foreground px-2 py-1.5 border-b border-border whitespace-nowrap relative bg-muted align-top"
                    style={width ? { width, minWidth: width, maxWidth: width } : undefined}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleSort(column)}
                        className="flex min-w-0 items-center gap-1 truncate hover:text-primary"
                        title={`Sort by ${column}`}
                      >
                        <span className="truncate">{column}{isTimeColumn(column) ? ' [UTC]' : ''}</span>
                        <span className={sorted ? 'text-primary' : 'text-muted-foreground/60'}>{sortIndicator}</span>
                      </button>
                      {active && <span className="text-blue-600 dark:text-blue-300" title="Filter active">●</span>}
                      {(canFilterColumns || canResizeColumns) && (
                        <button
                          type="button"
                          className="ml-auto rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted"
                          onClick={() => setOpenColumnMenu(openColumnMenu === column ? null : column)}
                          title={`Column options for ${column}`}
                        >
                          ⋯
                        </button>
                      )}
                    </div>

                    {openColumnMenu === column && (
                      <div className="absolute left-2 top-full mt-1 z-50 w-72 rounded-lg border border-border bg-background text-foreground shadow-xl p-3 font-normal whitespace-normal">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="font-bold text-sm truncate" title={column}>{column}</div>
                          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpenColumnMenu(null)}>Close</button>
                        </div>

                        {canFilterColumns && (
                          <div className="space-y-2 border-b border-border pb-3 mb-3">
                            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              <span>Filter</span>
                              {active && <button type="button" className="ml-auto normal-case underline" onClick={() => clearFilter(column)}>Clear</button>}
                            </div>
                            <select
                              className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                              value={(filter ?? defaultFilter()).operator}
                              onChange={(event) => patchFilter(column, { operator: event.target.value as FilterOperator })}
                            >
                              {FILTER_OPERATORS.map((operator) => (
                                <option key={operator.value} value={operator.value}>{operator.label}</option>
                              ))}
                            </select>

                            {!['is_empty', 'is_not_empty'].includes((filter ?? defaultFilter()).operator) && (
                              <input
                                className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                                placeholder="Filter..."
                                value={(filter ?? defaultFilter()).text}
                                onChange={(event) => patchFilter(column, { text: event.target.value })}
                              />
                            )}

                            <input
                              className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                              placeholder="Search values..."
                              value={valueSearch[column] ?? ''}
                              onChange={(event) => setValueSearch((prev) => ({ ...prev, [column]: event.target.value }))}
                            />

                            <div className="flex gap-2 text-xs">
                              <button type="button" className="underline" onClick={() => patchFilter(column, { selectedValues: null })}>Select all</button>
                              <button type="button" className="underline" onClick={() => patchFilter(column, { selectedValues: [] })}>Clear all</button>
                            </div>

                            <div className="max-h-44 overflow-auto border border-border rounded bg-muted/20 p-1 space-y-1">
                              {allValues
                                .filter((value) => value.toLowerCase().includes((valueSearch[column] ?? '').toLowerCase()))
                                .slice(0, 150)
                                .map((value) => {
                                  const selectedValues = filter?.selectedValues ?? null;
                                  const checked = selectedValues === null || selectedValues.includes(value);
                                  return (
                                    <label key={`${column}-${value}`} className="flex items-start gap-2 px-1.5 py-1 rounded hover:bg-background cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={checked}
                                        onChange={() => toggleValue(column, value)}
                                      />
                                      <span className="break-all text-xs">{value || <em className="text-muted-foreground">(empty)</em>}</span>
                                    </label>
                                  );
                                })}
                              {allValues.length === 0 && <div className="text-xs text-muted-foreground px-1.5 py-1">No values found.</div>}
                              {allValues.length > 150 && <div className="text-[11px] text-muted-foreground px-1.5 py-1">Showing first 150 values. Use search to narrow.</div>}
                            </div>
                          </div>
                        )}

                        {canResizeColumns && (
                          <div className="space-y-1 text-xs">
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={() => autosizeColumn(column)}>Autosize this column</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={autosizeAllColumns}>Autosize all columns</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={() => resetColumn(column)}>Reset this column</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={resetAllColumns}>Reset all columns</button>
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No rows match the current column filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row, rowIndex) => {
                const expanded = expandedRows.has(rowIndex);
                return (
                  <Fragment key={rowIndex}>
                    <tr>
                      <td className="w-7 border-b border-border px-1 py-1 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRowExpanded(rowIndex)}
                          className="flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground"
                          title={expanded ? 'Collapse row' : 'Expand row'}
                          aria-expanded={expanded}
                        >
                          {expanded ? '▾' : '▸'}
                        </button>
                      </td>
                      {columns.map((column, columnIndex) => {
                        const width = columnWidths[column];
                        const cellText = formatCell(column, row[columnIndex]);
                        const cellKey = `${rowIndex}-${column}`;
                        const copied = copiedKey === cellKey;
                        return (
                          <td
                            key={cellKey}
                            onClick={() => copyCell(cellKey, cellText)}
                            title="Click to copy"
                            className={`relative px-2 py-1 border-b border-border align-top max-w-[650px] whitespace-pre-wrap break-words cursor-pointer ${copied ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-blue-50/60 dark:hover:bg-blue-950/20'}`}
                            style={width ? { width, minWidth: width, maxWidth: width } : undefined}
                          >
                            {cellText}
                            {copied && (
                              <span className="absolute right-1 top-1 rounded bg-blue-600 px-1 py-0.5 text-[9px] font-semibold text-white shadow">Copied</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {expanded && (
                      <tr className="bg-muted/20">
                        <td colSpan={columns.length + 1} className="border-b border-border p-0">
                          <div className="divide-y divide-border">
                            {columns.map((column, columnIndex) => {
                              const rawText = stringifyCell(row[columnIndex]);
                              const cellKey = `expanded-${rowIndex}-${column}`;
                              const copied = copiedKey === cellKey;
                              return (
                                <div
                                  key={column}
                                  onClick={() => copyCell(cellKey, rawText)}
                                  title="Click to copy"
                                  className={`relative flex gap-4 px-3 py-1.5 cursor-pointer ${copied ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-blue-50/60 dark:hover:bg-blue-950/20'}`}
                                >
                                  <span className="w-48 flex-shrink-0 font-semibold text-muted-foreground">{column}{isTimeColumn(column) ? ' [UTC]' : ''}</span>
                                  <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{rawText || <em className="text-muted-foreground">(empty)</em>}</span>
                                  {copied && (
                                    <span className="absolute right-1 top-1 rounded bg-blue-600 px-1 py-0.5 text-[9px] font-semibold text-white shadow">Copied</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
